import gql from "graphql-tag";
import * as React from "react";

import {isTruthy} from "@khanacademy/wonder-stuff-core";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";

import ApolloFastlyQuery from "#data-access/gql/apollo-fastly-query.tsx";
import type {AsyncApolloFastlyQueryResult} from "#data-access/gql/apollo-types.ts";

import type {
    localesForLangPickerQuery_localesForLanguagePicker as GqlLocale,
    localesForLangPickerQuery,
} from "./__graphql-types__/localesForLangPickerQuery.ts";

/**
 * Result mapped to the component props
 */
export type LocalesForLangPickerQueryResult = {
    loading: boolean;
    error: Error | null | undefined;
    locales: Array<Locale>;
};

/**
 * An item containing the info for a given language
 */
export type Locale = {
    id: string;
    localName: string;
    kaLocale: string;
};

type Props = {
    children: (result: LocalesForLangPickerQueryResult) => React.ReactNode;
};

/**
 * A query component in charge of getting the list of locales used in the
 * language picker component
 */
class LanguagePickerQuery extends React.Component<Props> {
    render(): React.ReactNode {
        return (
            // We use PERSIST_ACROSS_PUBLISH as we only add new languages via
            // a deploy, not via a publish.
            <ApolloFastlyQuery
                query={DATA_QUERY}
                fastlyCaching="PERSIST_ACROSS_PUBLISH"
            >
                {(result) => this.props.children(mapDataToResult(result))}
            </ApolloFastlyQuery>
        );
    }
}

/**
 * Converts the query result to the component props
 * @param {QueryResult} result - The data received from the server
 *
 * @returns {LocalesForLangPickerQueryResult} The data ready to be consumed by
 * the child component
 */
const mapDataToResult = ({
    error,
    loading,
    data,
}: AsyncApolloFastlyQueryResult<localesForLangPickerQuery>): LocalesForLangPickerQueryResult => {
    if (loading || error || !data?.localesForLanguagePicker) {
        return {
            loading,
            error,
            locales: [],
        };
    }

    // sort languages by localName
    const locales = [...data.localesForLanguagePicker]
        .sort(
            (a?: GqlLocale | null, b?: GqlLocale | null) =>
                a?.localName?.localeCompare(b?.localName ?? "") ?? 0,
        )
        .filter(isTruthy)
        .map((locale) => ({
            id: locale?.id ?? "",
            localName: locale?.localName ?? "",
            kaLocale: locale?.kaLocale ?? "",
        }));

    return {
        loading,
        error,
        locales,
    };
};

export const DATA_QUERY: GqlOperation<localesForLangPickerQuery> = gqlOp(gql`
    query localesForLangPickerQuery {
        localesForLanguagePicker(includeEnglish: true, includeFake: false) {
            id
            localName
            kaLocale
        }
    }
`);

export default LanguagePickerQuery;
